import { useMemo, useState } from 'react';
import { useMutation, useQueries, useQueryClient } from '@tanstack/react-query';

import { gql } from '../../lib/api';
import {
  DELETE_LABOR_COST_ITEM_MUTATION,
  LABOR_COST_ITEMS_QUERY,
  SET_LABOR_COST_ITEM_MUTATION,
} from '../../graphql/queries';

interface Store {
  id: string;
  name: string;
}

interface LaborCostItem {
  storeId: string;
  itemKey: string;
  itemName: string;
  amount: number;
  notes: string | null;
  updatedAt: string | null;
}

interface ItemDef {
  key: string;
  name: string;
}

const DEFAULT_ITEMS: ItemDef[] = [
  { key: 'regular_salary', name: '正社員給与' },
  { key: 'part_time', name: 'アルバイト・パート給与' },
  { key: 'social_insurance', name: '法定福利費' },
  { key: 'welfare', name: '福利厚生費' },
];

const slugify = (s: string): string =>
  s
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 32) || `item_${Date.now()}`;

interface Props {
  stores: Store[];
}

export const LaborBudgetSection = ({ stores }: Props) => {
  const qc = useQueryClient();

  const itemQueries = useQueries({
    queries: stores.map((s) => ({
      queryKey: ['laborCostItems', s.id],
      queryFn: () =>
        gql<{ laborCostItems: LaborCostItem[] }>(LABOR_COST_ITEMS_QUERY, { storeId: s.id }),
      enabled: !!s.id,
    })),
  });

  const itemMap = useMemo(() => {
    const m: Record<string, Record<string, { itemName: string; amount: number }>> = {};
    stores.forEach((s, i) => {
      m[s.id] = {};
      const data = itemQueries[i]?.data?.laborCostItems ?? [];
      for (const e of data) m[s.id][e.itemKey] = { itemName: e.itemName, amount: e.amount };
    });
    return m;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stores, itemQueries.map((q) => q.dataUpdatedAt).join('|')]);

  const [extraItems, setExtraItems] = useState<ItemDef[]>([]);
  const items: ItemDef[] = useMemo(() => {
    const known = new Set(DEFAULT_ITEMS.map((it) => it.key));
    const extras: ItemDef[] = [];
    Object.values(itemMap).forEach((m) => {
      Object.entries(m).forEach(([k, v]) => {
        if (!known.has(k) && !extras.find((e) => e.key === k)) {
          extras.push({ key: k, name: v.itemName });
          known.add(k);
        }
      });
    });
    extraItems.forEach((it) => {
      if (!known.has(it.key)) {
        extras.push(it);
        known.add(it.key);
      }
    });
    return [...DEFAULT_ITEMS, ...extras];
  }, [itemMap, extraItems]);

  const [draft, setDraft] = useState<Record<string, Record<string, string>>>({});

  const setItem = useMutation({
    mutationFn: (vars: { storeId: string; input: { itemKey: string; itemName: string; amount: number } }) =>
      gql(SET_LABOR_COST_ITEM_MUTATION, vars),
    onSuccess: (_, vars) => {
      void qc.invalidateQueries({ queryKey: ['laborCostItems', vars.storeId] });
    },
  });

  const deleteItem = useMutation({
    mutationFn: (vars: { storeId: string; itemKey: string }) =>
      gql(DELETE_LABOR_COST_ITEM_MUTATION, vars),
    onSuccess: (_, vars) => {
      void qc.invalidateQueries({ queryKey: ['laborCostItems', vars.storeId] });
    },
  });

  const getValue = (storeId: string, itemKey: string): string => {
    const d = draft[storeId]?.[itemKey];
    if (d !== undefined) return d;
    const v = itemMap[storeId]?.[itemKey]?.amount;
    return v != null ? String(v) : '';
  };

  const updateDraft = (storeId: string, itemKey: string, value: string) => {
    setDraft((prev) => ({
      ...prev,
      [storeId]: { ...prev[storeId], [itemKey]: value },
    }));
  };

  const saveCell = (storeId: string, itemKey: string, itemName: string) => {
    const value = draft[storeId]?.[itemKey];
    if (value === undefined || value === '') return;
    const num = Number(value);
    if (!Number.isFinite(num) || num < 0) {
      alert('金額は 0 以上の数値で入力してください。');
      return;
    }
    if (num === itemMap[storeId]?.[itemKey]?.amount) {
      setDraft((prev) => {
        const next = { ...prev };
        if (next[storeId]) {
          const cell = { ...next[storeId] };
          delete cell[itemKey];
          next[storeId] = cell;
        }
        return next;
      });
      return;
    }
    setItem.mutate({
      storeId,
      input: { itemKey, itemName, amount: num },
    });
  };

  const addItem = () => {
    const name = prompt('追加する人件費項目を入力してください（例：賞与）');
    if (!name) return;
    const baseKey = slugify(name);
    let key = baseKey;
    let n = 1;
    const existing = new Set(items.map((it) => it.key));
    while (existing.has(key)) {
      n += 1;
      key = `${baseKey}_${n}`;
    }
    setExtraItems((prev) => [...prev, { key, name }]);
  };

  const removeItem = (item: ItemDef) => {
    if (!confirm(`「${item.name}」を全店舗から削除します。よろしいですか？`)) return;
    for (const s of stores) {
      if (itemMap[s.id]?.[item.key] != null) {
        deleteItem.mutate({ storeId: s.id, itemKey: item.key });
      }
    }
    setExtraItems((prev) => prev.filter((it) => it.key !== item.key));
  };

  const grandTotal = stores.reduce(
    (s, st) =>
      s +
      items.reduce((m, it) => {
        const v = Number(getValue(st.id, it.key));
        return m + (Number.isFinite(v) ? v : 0);
      }, 0),
    0,
  );

  return (
    <section style={card}>
      <div style={sectionHeader}>
        <h2 style={sectionTitle}>
          月額固定値（暫定、freee 連携までの仮値）
          <span style={{ marginLeft: 8, fontSize: '0.72rem', fontWeight: 400, color: '#868e96' }}>
            登録があればトップページの人件費 KPI はこちらを優先
          </span>
        </h2>
        <button type="button" onClick={addItem} style={secondaryBtn}>
          ＋ 項目を追加
        </button>
      </div>

      {grandTotal === 0 ? (
        <p style={muted}>
          登録なし — トップページの人件費 KPI は Airシフト × スタッフマスタからの実労働ベース算出を使用します。
        </p>
      ) : (
        <p style={muted}>
          全社月額合計 <strong style={{ color: '#212529' }}>¥{grandTotal.toLocaleString()}</strong>
        </p>
      )}

      <div style={{ overflowX: 'auto', marginTop: 8 }}>
        <table style={tableStyle}>
          <thead>
            <tr>
              <th
                style={{
                  ...th,
                  minWidth: 180,
                  position: 'sticky',
                  left: 0,
                  background: '#f8f9fa',
                  zIndex: 1,
                }}
              >
                項目
              </th>
              {stores.map((s) => (
                <th key={s.id} style={{ ...th, textAlign: 'right', minWidth: 140 }}>
                  {s.name}
                </th>
              ))}
              <th style={{ ...th, textAlign: 'right', minWidth: 140, background: '#fff5f5' }}>
                全社合計
              </th>
              <th style={{ ...th, width: 60 }}></th>
            </tr>
          </thead>
          <tbody>
            {items.map((it) => {
              const rowTotal = stores.reduce((sum, s) => {
                const v = Number(getValue(s.id, it.key));
                return sum + (Number.isFinite(v) ? v : 0);
              }, 0);
              return (
                <tr key={it.key}>
                  <td
                    style={{
                      ...td,
                      minWidth: 180,
                      position: 'sticky',
                      left: 0,
                      background: '#fff',
                      zIndex: 1,
                    }}
                  >
                    {it.name}
                  </td>
                  {stores.map((s, i) => (
                    <td key={s.id} style={{ ...td, padding: '0.2rem' }}>
                      <input
                        type="number"
                        step={1000}
                        min={0}
                        value={getValue(s.id, it.key)}
                        onChange={(e) => updateDraft(s.id, it.key, e.target.value)}
                        onBlur={() => saveCell(s.id, it.key, it.name)}
                        style={cellInput}
                        placeholder="—"
                        disabled={itemQueries[i]?.isLoading}
                      />
                    </td>
                  ))}
                  <td style={{ ...td, textAlign: 'right', fontWeight: 700, background: '#fff5f5' }}>
                    ¥{rowTotal.toLocaleString()}
                  </td>
                  <td style={{ ...td, textAlign: 'center', padding: '0.2rem' }}>
                    <button
                      type="button"
                      onClick={() => removeItem(it)}
                      title={`${it.name} を削除`}
                      style={removeBtn}
                    >
                      ×
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr>
              <td
                style={{
                  ...td,
                  fontWeight: 700,
                  background: '#f8f9fa',
                  position: 'sticky',
                  left: 0,
                  zIndex: 1,
                }}
              >
                店舗別 月額合計
              </td>
              {stores.map((s) => {
                const total = items.reduce((sum, it) => {
                  const v = Number(getValue(s.id, it.key));
                  return sum + (Number.isFinite(v) ? v : 0);
                }, 0);
                return (
                  <td
                    key={s.id}
                    style={{ ...td, textAlign: 'right', fontWeight: 700, background: '#f8f9fa' }}
                  >
                    ¥{total.toLocaleString()}
                  </td>
                );
              })}
              <td
                style={{
                  ...td,
                  textAlign: 'right',
                  fontWeight: 700,
                  background: '#aa0b56',
                  color: '#fff',
                }}
              >
                ¥{grandTotal.toLocaleString()}
              </td>
              <td style={{ ...td, background: '#f8f9fa' }}></td>
            </tr>
          </tfoot>
        </table>
      </div>
      {(setItem.isError || deleteItem.isError) && (
        <p style={{ color: '#aa0b56', marginTop: '0.5rem' }}>
          保存エラー：
          {((setItem.error as Error | null) ?? (deleteItem.error as Error | null))?.message}
        </p>
      )}
    </section>
  );
};

const card: React.CSSProperties = {
  background: '#fff',
  borderRadius: 2,
  padding: '1rem 1.25rem',
  boxShadow: 'none',
  border: '1px solid #d4d1cf',
  marginBottom: '1rem',
};
const sectionHeader: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  flexWrap: 'wrap',
  gap: '0.5rem',
};
const sectionTitle: React.CSSProperties = {
  fontSize: '0.95rem',
  marginTop: 0,
  marginBottom: 0,
  color: '#495057',
};
const muted: React.CSSProperties = { color: '#868e96', fontSize: '0.85rem', margin: '0.5rem 0 0' };
const cellInput: React.CSSProperties = {
  padding: '0.3rem 0.4rem',
  borderRadius: 4,
  border: '1px solid #d4d1cf',
  width: '100%',
  textAlign: 'right',
  fontSize: '0.85rem',
  background: '#fff',
};
const secondaryBtn: React.CSSProperties = {
  padding: '0.4rem 0.7rem',
  borderRadius: 6,
  border: '1px solid #ced4da',
  background: '#fff',
  color: '#495057',
  cursor: 'pointer',
  fontSize: '0.82rem',
};
const removeBtn: React.CSSProperties = {
  border: 'none',
  background: 'transparent',
  fontSize: '0.95rem',
  padding: '0 0.2rem',
  lineHeight: 1,
  color: '#aa0b56',
  cursor: 'pointer',
};
const tableStyle: React.CSSProperties = {
  width: '100%',
  borderCollapse: 'collapse',
  fontSize: '0.85rem',
};
const th: React.CSSProperties = {
  textAlign: 'left',
  padding: '0.5rem 0.6rem',
  borderBottom: '1px solid #dee2e6',
  background: '#f8f9fa',
  fontSize: '0.82rem',
};
const td: React.CSSProperties = {
  padding: '0.4rem 0.6rem',
  borderBottom: '1px solid #f1f3f5',
};
